import React, { useRef, useEffect, useState } from 'react';
import './GamePage.styles.scss';
import { AnimatePresence, motion } from 'framer-motion';
import { Game } from '../components/asteroids/Game';

import Button from '../components/buttons/button.component';
import { useDispatch, useSelector } from 'react-redux';
import useIsServer from '../hooks/useIsServer.hook';
import mainApi from '../redux/apis/main-api';
import { getLogedUser, setUser, userLogin } from '../redux/globals/globals.actions';
import { selectUser } from '../redux/globals/globals.selectors';
import SidebarContent from '../components/sideBar/SidebarContent';
import SideBar from '../components/sideBar/SideBar.component';
import Notifications from '../components/notifications/Notifications.component';
import { Tab, Tabber, TabMenu } from '../components/tabber/Tabber.component';
import Highscores from './screens/Highscores.section';
import PersonalScores from '../components/personalScores/PersonalScores.component';
import Container from '../components/layout/container.component';
import Popup from '../components/popup/Popup';
import Help from './Help.component';
import DriversList from './screens/Drivers.screen';
import DriverUI from './gameUI/Driver.ui';
import { TabberProvider } from '../components/tabber/Tabber.provider';
import Hangar from './screens/Hangar.screen';
import GameScreen from './screens/Game.screen';
import Header from '../components/header/Header';
import AuthModal from '../components/auth/AuthModal.component';
import Tournaments from './screens/Tournaments.screen';

const GamePage = (props) => {
	const canvasRef = useRef();
	const gameRef = useRef();
	const dispatch = useDispatch();
	const user = useSelector(selectUser);

	const [version, setVersion] = useState('0.84.0');
	const [userScores, setUserScores] = useState();

	//UI Data
	const [data, setData] = useState({
		oreBallance: 0,
		sessionOreCollected: 0
	});
	const [loading, setLoading] = useState(false);
	const [showAuthModal, setShowAuthModal] = useState(false);
	const [isPortrait, setIsPortrait] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	// Detect mobile device and orientation
	useEffect(() => {
		const checkMobile = () => {
			const mobile = window.innerWidth < 768 || 
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
			setIsMobile(mobile);
			return mobile;
		};
		
		const checkOrientation = (mobile) => {
			// Check if device is in portrait mode
			const portrait = window.innerHeight > window.innerWidth;
			setIsPortrait(portrait);
			
			// Note: We don't lock orientation here because users need to be able to log in in portrait mode
			// Orientation lock would be attempted only when user is logged in (handled elsewhere if needed)
		};
		
		const mobile = checkMobile();
		checkOrientation(mobile);
		
		const handleResize = () => {
			const mobile = checkMobile();
			checkOrientation(mobile);
		};
		
		const handleOrientationChange = () => {
			const mobile = window.innerWidth < 768 || 
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
			checkOrientation(mobile);
		};
		
		window.addEventListener('resize', handleResize);
		window.addEventListener('orientationchange', handleOrientationChange);
		
		// Also listen to screen orientation API if available
		if (screen.orientation) {
			screen.orientation.addEventListener('change', handleOrientationChange);
		}
		
		return () => {
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('orientationchange', handleOrientationChange);
			if (screen.orientation) {
				screen.orientation.removeEventListener('change', handleOrientationChange);
			}
		};
	}, []);

	useEffect(() => {
		initGame();

		// Check if user is already logged in
		if (!user) {
			dispatch(getLogedUser())
				.then((res) => {
					// Handle new response format: {success: true, data: {...}}
					const userData = res?.data?.data || res?.data;
					if (userData) {
						dispatch(setUser({ ...userData }));

						setData((prevState) => ({
							...prevState,
							...{ 
								oreBallance: typeof userData?.ballance === 'number' ? userData.ballance : parseFloat(userData?.ballance || 0),
								sessionOreCollected: 0
							},
						}));

						getUserScores();
					}
				})
				.catch((error) => {
					// 401 is expected when no session exists - this is fine, user can click "Play Now"
					if (error.response && error.response.status === 401) {
						// No user session - this is normal for first-time visitors
						console.log('No active session - user can play as guest');
					} else {
						console.error('Error checking user session:', error);
					}
				});
		} else {
			// User is already logged in, fetch their balance and scores
			// Load balance from user object if available
			if (user?.ballance !== undefined) {
				setData((prevState) => ({
					...prevState,
					oreBallance: typeof user.ballance === 'number' ? user.ballance : parseFloat(user.ballance || 0),
					sessionOreCollected: 0
				}));
			}
			getUserScores();
		}

		return () => {
			gameRef.current?.destroyGame();
		};
	}, []);

	useEffect(() => {
		console.log('USER US ', user);
		if (!user) {
			gameRef.current.disconnectFromServer();
		} else {
			gameRef.current.connectToServer();
			// Update balance from user object when user changes
			if (user?.ballance !== undefined) {
				setData((prevState) => ({
					...prevState,
					oreBallance: typeof user.ballance === 'number' ? user.ballance : parseFloat(user.ballance || 0)
				}));
			}
		}
	}, [user]);

	const getUserScores = async () => {
		try {
			let sessions = await mainApi.get('/APIS/user/mysessions');
			// Handle new response format: {success: true, data: {...}}
			const sessionsData = sessions.data?.data || sessions.data;
			
			// Map gameSessions to gs for PersonalScores component
			const mappedData = {
				highscore: sessionsData?.highscore || 0,
				gs: sessionsData?.gameSessions || []
			};
			
			setUserScores(mappedData);

			setData((prevState) => ({
				...prevState,
				...{ highscore: sessionsData?.highscore || 0 },
			}));
		} catch (err) {
			console.error('Failed to fetch user scores:', err);
			setUserScores({ highscore: 0, gs: [] });
		}
	};

	const playNow = async () => {
		setLoading(true);

		dispatch(userLogin({ playAsGuest: true }))
			.then((response) => {
				// Handle new response format: {success: true, data: {...}}
				const userData = response.data?.data || response.data;
				if (userData) {
					dispatch(setUser({ ...userData }));

					setData((prevState) => ({
						...prevState,
						...{ 
							oreBallance: typeof userData?.ballance === 'number' ? userData.ballance : parseFloat(userData?.ballance || 0),
							sessionOreCollected: 0
						},
					}));

					setLoading(false);

					getUserScores();
				}
			})
			.catch((error) => {
				console.error('Login error:', error);
				dispatch(setUser(null));
				setLoading(false);
			});
	};

	const initGame = () => {
		let g = new Game(
			canvasRef.current,
			() => {
				setTimeout(() => {
					getUserScores();
				}, 3000);
			},
			(data) => {
				setData((prevState) => ({
					...prevState,
					...data,
				}));
			},
			null // No wallet address needed
		);

		gameRef.current = g;
	};

	return (
		<>
			<TabberProvider>
				{/* Landscape-only overlay for mobile - only show when user is logged in */}
				{user && isMobile && isPortrait && (
					<motion.div 
						className="landscape-only-overlay"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<div className="landscape-message">
							<img className="logo" src="/assets/pr_logo_white.svg" alt="Pocket Rockets" />
							<div className="title">Please Rotate Your Device</div>
							<div className="message">
								Pocket Rockets is designed for<br />
								landscape mode for the best experience.
							</div>
							<div className="rotate-icon">↻</div>
						</div>
					</motion.div>
				)}
				
				<section className="gamepage">
					<AnimatePresence>
						{!data?.gameStarted && data?.gameLoaded && (
							<motion.div className="mainMenu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
								{!user && (
									<div className="connect">
										<div className="inner">
											<img className="logo" src="/assets/pr_logo_white.svg" />

											<div className="step one">
												<Button className="cta-glow" onClick={() => setShowAuthModal(true)}>
													Login / Create Account
												</Button>

												<div className="message">
													Create an account to save your progress across devices
												</div>

												<Button className="cta-glow secondary" isLoading={loading} onClick={playNow} style={{ marginTop: '20px' }}>
													Play as Guest
												</Button>

												<div className="message" style={{ marginTop: '20px', fontSize: '14px', opacity: 0.7 }}>
													<em>Guest accounts are temporary - create an account to save your progress</em>
												</div>
											</div>
										</div>
									</div>
								)}

								{user && (
									<div className={`mainScreen ${isMobile && isPortrait ? 'portrait-blocked' : ''}`}>
										<div className="header">
											<Header data={data} version={version} />
										</div>

										<Tabber>
											<Tab name="Play">
												<GameScreen
													data={data}
													startGame={() => {
														gameRef.current.startGame();
														canvasRef.current.focus();
													}}
												/>
											</Tab>

											<Tab name="Competitions">
												<Tournaments />
											</Tab>

											{/* <Tab name="Hangar">
												<Hangar />
											</Tab> */}

											<Tab name="Drivers">{user && <DriversList />}</Tab>

											<Tab name="Highscores">
												<Highscores />
											</Tab>

											<Tab name="Pilot profile">
												<div className="pilotProfileContainer">
													<Container>
														<div className="pilotProfile">
															<div>
																<SidebarContent />
															</div>
															<div>
																<div className="highscore">
																	<div className="title">Your all time highscore</div>
																	<div className="score">{data?.highscore}</div>
																</div>
															</div>
															<div>
																<PersonalScores data={userScores} />
															</div>
														</div>
													</Container>
												</div>
											</Tab>
										</Tabber>
									</div>
								)}
							</motion.div>
						)}
					</AnimatePresence>

					<div className="gradient" />
					<div className="back-gradient" />

					<AnimatePresence>
						<canvas ref={canvasRef} tabIndex="1" className="game-holder" style={{ opacity: data?.gameStarted ? 1 : 0 }} />
					</AnimatePresence>

					<AnimatePresence>
						{showAuthModal && (
							<AuthModal
								onGuestPlay={() => {
									setShowAuthModal(false);
									playNow();
								}}
								onClose={() => setShowAuthModal(false)}
							/>
						)}
					</AnimatePresence>

					<AnimatePresence>
						{data?.gameStarted && (
							<>
								<AnimatePresence>
									{data.ore > 0 && data.platformUp && data.shipOnPlatform && (
										<motion.div
											initial={{ y: -20, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -20, opacity: 0 }}
											transition={{ duration: 1 }}
											key="3"
											className="oreExtractionHolder"
										>
											<div className="timer"> {data.secondsLeft}</div>
											<div className="desc">
												Ore extraction in progress. <br />
												You are safe.
											</div>
											<div className="triangle"></div>
										</motion.div>
									)}
								</AnimatePresence>

								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.5 }}
									className="top-panel"
								>
									<div className="player">PLAYER {user?.id_users || 'GUEST'}</div>
									<div className="score-title">YOUR SCORE</div>

									<motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} key={data?.score} className="yourScore">
										{data?.score}
									</motion.div>

									<div className="highscore">
										Your highscore<div className="score">{userScores?.highscore}</div>
									</div>

									<div className="side-bar">
										<motion.div
											className="healthbar"
											initial={{ opacity: 0, x: -50 }}
											animate={{ opacity: 1, x: 0 }}
											transition={{ delay: 0.8, duration: 0.5 }}
										>
											<div className="health" style={{ height: data?.health + '%' }}></div>
											<div className="healthNum">{data?.health}%</div>
											<div className="title">SHIP'S SHIELD HEALTH</div>
										</motion.div>

										<motion.div
											className="oreStash"
											initial={{ opacity: 0, x: -50 }}
											animate={{ opacity: 1, x: 0 }}
											transition={{ delay: 0.8, duration: 0.5 }}
										>
											<div className="ore" style={{ height: data?.orePerc + '%' }}></div>
											<div className="oreNum">{`${data?.ore} COLLECTED`}</div>
											<div className="title">ORE CONTAINER</div>

											<AnimatePresence>
												{data.ore > 0 && !data.platformUp && (
													<div className="oreNotification">
														<motion.div
															key="1"
															initial={{ y: -20, opacity: 0 }}
															animate={{ y: 0, opacity: 1 }}
															exit={{ y: -20, opacity: 0 }}
															transition={{ duration: 0.7 }}
														>
															PRESS <span>F</span> TO CALL PLATFORM
														</motion.div>
													</div>
												)}

												{data.ore > 0 && data.platformUp && (
													<div className="oreNotification">
														<motion.div
															key="2"
															initial={{ y: -20, opacity: 0 }}
															animate={{ y: 0, opacity: 1 }}
															exit={{ y: -20, opacity: 0 }}
															transition={{ duration: 0.7 }}
														>
															PARK YOUR SHIP ON PLATFORM
														</motion.div>
													</div>
												)}
											</AnimatePresence>
										</motion.div>
									</div>
								</motion.div>

								<motion.div
									initial={{ opacity: 0, y: 40 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.8, duration: 0.5 }}
									className="bottom-panel"
								>
									<DriverUI />

									<div className="upgrades">
										<div className="title">SHIPS UPGRADES (comming soon)</div>
										<div className="upgrade empty"></div>
										<div className="upgrade empty"></div>
										<div className="upgrade empty"></div>
										<div className="upgrade empty"></div>
									</div>
								</motion.div>

								<motion.div
									initial={{ opacity: 0, y: 40 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.5 }}
									className="top-right-panel"
								>
									<motion.div
										initial={{ scale: 0.6 }}
										animate={{ scale: 1 }}
										transition={{ duration: 0.6 }}
										key={`${data?.oreBallance}-${data?.sessionOreCollected}`}
										className="ore"
									>
										<img className="image" src="/assets/ore_icon.png" />
										<div className="data">
											<div className="title">$ORE</div>
											<div className="ore-collected">
												<span className="session-ore-yellow">{data?.sessionOreCollected ?? 0}</span>
												<span className="total-ore-blue"> ({data?.oreBallance ?? 0})</span>
											</div>
										</div>
									</motion.div>

									{/* <div className="stats-message">
									<div className={`status ${data.server ? 'online' : 'offline'}`}>
										STATUS : <span>{`${data.server ? 'CONNECTED' : 'OFFLINE'}`}</span>
									</div>
								</div> */}
								</motion.div>
							</>
						)}

						{data?.gameOver && (
							<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="gameOverScreen">
								<div className="window">
									<div className="inner">
										<div className="title">You died</div>
										<div className="contentRow">
											<div className="scoresContainer">
												{data?.oreCollected !== undefined && (
													<div className="oreCollected">
														<div className="label">$ORE COLLECTED</div>
														<div className="value">{data.oreCollected}</div>
													</div>
												)}
												{data?.score !== undefined && (
													<div className="finalScore">
														<div className="label">FINAL SCORE</div>
														<div className="value">{data.score}</div>
													</div>
												)}
											</div>
											<div className="buttonsContainer">
												<div
													className="restart"
													onClick={() => {
														gameRef.current.startGame();
														canvasRef.current.focus();
													}}
												>
													Play again
												</div>
												<div
													className="mainmenu"
													onClick={() => {
														setData((prevState) => ({
															...prevState,
															...{ gameStarted: false, gameOver: false },
														}));
													}}
												>
													Back to main screen
												</div>
											</div>
										</div>
									</div>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</section>
			</TabberProvider>
		</>
	);
};

export default GamePage;
